import { useQuery } from '@apollo/client'
import { Hidden, useMediaQuery, useTheme } from '@material-ui/core'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'

import {
  buildFeatureSet,
  getCredentials,
  hasRequiredFeatures
} from '../../cache/auth'
import PageHeader from '../../components/PageHeader'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import accommodationPagesRoleManagement from '../../staticRoleManagement/accommodationsRoleManagement'
import ratePlanPagesRoleManagement from '../../staticRoleManagement/ratePlanRoleManagement'
import roomTypePagesRoleManagement from '../../staticRoleManagement/roomTypeRoleManagement'
import { Accommodation } from '../../types/accommodation'
import AccommodationsListHeader from './AccommodationsListHeader'
import { AccommodationsTable } from './AccommodationsTable'
import { AccommodationsTableMobile } from './AccommodationsTableMobile'
import useStyles from './styles/index.styles'

const AccommodationsList: React.FC = () => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filteredAccommodations, setFilteredAccommodations] = useState<
    Accommodation[]
  >([])

  const { loading, data, refetch } = useQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      showLog: true
    }
  })

  useEffect(() => {
    setPage(1)
    const fullAccommodationsList = data?.accommodations
      ? data?.accommodations
      : []
    const newFilteredAccommodations = searchInputValue
      ? matchSorter(fullAccommodationsList, searchInputValue, {
          keys: [
            'basicDetails.name',
            {
              key: 'basicDetails.displayPropertyCode',
              threshold: matchSorter.rankings.STARTS_WITH
            }
          ]
        })
      : fullAccommodationsList

    setFilteredAccommodations(newFilteredAccommodations)
    setcount(newFilteredAccommodations.length)
  }, [searchInputValue, loading])

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const hasAccessToViewRoomType = roomTypePagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/room-types/list'
    )
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const hasAccessToViewRatePlan = ratePlanPagesRoleManagement
    .find(
      (item) =>
        item.route ===
        '/accommodations/:accommodationID/:propertyCode/rate-plans/list'
    )
    ?.rolesListWithReadAccess?.includes(getRoleFromStorage())

  const RoleAccessToViewAccommodationDeatils = hasRequiredFeatures(
    accommodationPagesRoleManagement.find(
      (item) => item.route === '/accommodations'
    )?.sections?.SELECT_ACCOMMODATION.requiredFeatures,
    featureSet
  )

  const RoleAccessToViewImportData = hasRequiredFeatures(
    accommodationPagesRoleManagement.find(
      (item) => item.route === '/accommodations'
    )?.sections?.IMPORT_DATA.requiredFeatures,
    featureSet
  )

  return (
    <div>
      <PageHeader title={getTranslate('Accommodations')} />
      <AccommodationsListHeader
        inputValue={searchInputValue}
        setinputVal={setsearchInputValue}
      />
      <Hidden smDown>
        <AccommodationsTable
          hasAccessToViewRoomType={hasAccessToViewRoomType}
          hasAccessToViewRatePlan={hasAccessToViewRatePlan}
          RoleAccessToViewAccommodationDeatils={
            RoleAccessToViewAccommodationDeatils
          }
          RoleAccessToViewImportData={RoleAccessToViewImportData}
          data={filteredAccommodations.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loading={loading}
          reloadData={refetch}
        />
      </Hidden>
      <Hidden mdUp>
        <AccommodationsTableMobile
          hasAccessToViewRoomType={hasAccessToViewRoomType}
          hasAccessToViewRatePlan={hasAccessToViewRatePlan}
          RoleAccessToViewAccommodationDeatils={
            RoleAccessToViewAccommodationDeatils
          }
          RoleAccessToViewImportData={RoleAccessToViewImportData}
          data={filteredAccommodations.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loading={loading}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          size={isSmallScreen ? 'small' : 'medium'}
          page={page}
          onChange={(_, value) => setPage(value)}
        />
      </div>
    </div>
  )
}

export default AccommodationsList
